// /login — sign in with email + password. When the password is correct, a one-time code is emailed
// (two-factor). Staff may also use Google SSO if configured. Accounts must be registered by invite.
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout.jsx';
import { login, fetchProviders, requestPasswordReset, GOOGLE_LOGIN_URL } from '../api/auth.js';

const ERROR_MESSAGES = {
  google_denied: 'Google sign-in was cancelled.',
  DOMAIN_NOT_ALLOWED: 'Only @teamm8.com Google accounts can sign in as staff.',
  ACCOUNT_INACTIVE: 'Your account is inactive. Contact TEAMM8 wholesale support.',
  NOT_REGISTERED: 'This account is not registered yet. Check your inbox for an invitation.',
  OAUTH_STATE_INVALID: 'That sign-in link expired. Please try again.',
  google: 'Google sign-in failed. Please try again.',
};

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(location.state?.notice || (location.state?.registered ? 'Account activated — sign in below.' : ''));
  // Arriving from an expired reset link (or "Get a new code") lands straight in the right mode
  // instead of on a blank sign-in form the user has to re-navigate.
  const [mode, setMode] = useState(location.state?.forgot ? 'reset' : 'login'); // 'login' | 'reset'
  const [providers, setProviders] = useState({ google: false, otp: true });

  useEffect(() => {
    fetchProviders()
      .then(setProviders)
      .catch(() => setProviders({ google: false, otp: true }));
  }, []);

  useEffect(() => {
    const code = params.get('error');
    if (code) setError(ERROR_MESSAGES[code] || 'Sign-in failed. Please try again.');
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmed = email.trim();
      await login(trimmed, password); // verifies password, then emails a one-time code
      navigate('/login/verify', { state: { email: trimmed } });
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot password: request a reset link. The response is identical whether or not the email exists,
  // so we always show the same confirmation (no account enumeration) — even if the request errors.
  const submitReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const trimmed = email.trim();
    try {
      await requestPasswordReset(trimmed);
    } catch {
      /* swallow — never reveal whether the account exists */
    } finally {
      setLoading(false);
      setPassword('');
      setMode('login');
      setNotice(`If an account exists for ${trimmed}, we’ve emailed a link to set a new password. Check your inbox (and spam).`);
    }
  };

  if (mode === 'reset') {
    return (
      <AuthLayout>
        <h1 className="text-lg font-semibold text-text-primary">Reset password</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Enter your email and we’ll send you a link to set a new password.
        </p>
        <form onSubmit={submitReset} className="mt-5 space-y-4">
          <div>
            <label htmlFor="reset-email" className="block text-sm font-medium text-text-primary">
              Email
            </label>
            <input
              id="reset-email"
              type="email"
              name="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="mt-1.5 w-full rounded-md border border-line bg-surface px-3 py-2.5 text-sm text-text-primary outline-none transition placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </div>
          {error && <p className="text-sm text-error">{error}</p>}
          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="w-full rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-white transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('login');
              setError('');
            }}
            className="w-full text-center text-xs text-text-muted transition hover:text-text-primary"
          >
            Back to sign in
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="text-lg font-semibold text-text-primary">Sign in</h1>
      <p className="mt-1 text-sm text-text-secondary">TEAMM8 Wholesale ordering portal.</p>
      {notice && <p className="mt-3 rounded-md bg-success-light px-3 py-2 text-sm text-success">{notice}</p>}


      <form onSubmit={submit} className="mt-5 space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-text-primary">
            Email
          </label>
          <input
            id="email"
            type="email"
            name="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="mt-1.5 w-full rounded-md border border-line bg-surface px-3 py-2.5 text-sm text-text-primary outline-none transition placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/30"
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-text-primary">
            Password
          </label>
          <input
            id="password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            className="mt-1.5 w-full rounded-md border border-line bg-surface px-3 py-2.5 text-sm text-text-primary outline-none transition placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/30"
          />
          <div className="mt-1.5 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setMode('reset');
                setError('');
                setNotice('');
              }}
              className="text-xs text-accent transition hover:underline"
            >
              Forgot password?
            </button>
          </div>
        </div>
        {error && <p className="text-sm text-error">{error}</p>}
        <button
          type="submit"
          disabled={loading || !email.trim() || !password}
          className="w-full rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-white transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Continue'}
        </button>
        <p className="text-center text-xs text-text-muted">We’ll email you a one-time code to finish signing in.</p>
      </form>

      {/* Staff-only: Google sign-in for @teamm8.com accounts. Placed below (and labelled) so buyers and
          reps — who sign in with a password + code — aren't sent down a path that can't work for them. */}
      {providers.google && (
        <div className="mt-6 border-t border-line pt-4">
          <a
            href={GOOGLE_LOGIN_URL}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-line bg-surface px-4 py-2 text-sm font-medium text-text-secondary transition hover:bg-surface-raised"
          >
            <GoogleGlyph />
            TEAMM8 staff: sign in with Google
          </a>
        </div>
      )}
    </AuthLayout>
  );
}

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"
      />
    </svg>
  );
}
